<script>
import { SancareOcticon } from '@sancare/typescript-commons'
import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import _ from 'lodash'
import { mapActions, mapState } from 'vuex'

import filtersData from '../filters/FiltersData'
import { getDocumentCategoryLabel } from '../stay-displayer/health-data/documentLabels'
import { documentCategories, textualHealthDataCategories } from '../stay-displayer/health-data/documentLabels'
import { DOCUMENT_CRITERIA_TYPES, getAutomationExplanation } from './automationGroupCriteriaExplanation'

export default {
  components: {
    'sancare-octicon': SancareOcticon,
  },
  props: {
    automationGroup: { type: Object, required: true },
    stay: { type: Object, required: true }
  },
  data() {
    return {
      criteriaData: filtersData,
      textualReportCategories: { ...documentCategories, ...textualHealthDataCategories },
    }
  },
  computed: {
    ...mapState({
      pmsiCountry: (state) => state.settings.pmsi.country,
      automationDocumentCriteriaMatches: (state) => state.automation.automationDocumentCriteriaMatches,
      explainAutomationDocumentCriteriaRequest: (state) => state.automation.explainAutomationDocumentCriteriaRequest,
      waitingForAnesthesia: (state) => state.automation.isWaitingForAnesthesia,
      waitingForAnapath: (state) => state.automation.isWaitingForAnapath,
      hasTextualCriteria: (state) => state.automation.hasTextualCriteria,
    }),
    automationDocumentCriteriaKey() {
      return `${this.stay.id}-${this.automationGroup.id}`
    }
  },
  watch: {
    automationDocumentCriteriaKey: {
      immediate: true,
      handler() {
        this.explainAutomationDocumentCriteria({ stayId: this.stay.id, automationGroupId: this.automationGroup.id })
      }
    }
  },
  methods: {
    ...mapActions(['explainAutomationDocumentCriteria']),
    getCriteriaType(criteriaType) {
      return this.$t(`filter.labels.${criteriaType}`)
    },
    getAdmissionOrReleaseModeLabel(criteria, country) {
      return _.find(
        this.criteriaData[criteria.type]['options'], function(option) {
          return option.value === criteria.value && option.country === country
        }).label
    },
    getCriteriaValue(criteria) {

      switch (criteria.type) {
        case 'documentCategory':
          return getDocumentCategoryLabel(criteria.value)
        case 'admissionMode':
        case 'releaseMode':
        case 'origin':
        case 'destination':
          return this.getAdmissionOrReleaseModeLabel(criteria, this.pmsiCountry)
        case 'gender':
          return {
            1: this.$t('automation.validationConditions.male'),
            2: this.$t('automation.validationConditions.female'),
            3: this.$t('automation.validationConditions.undetermined'),
          }[criteria.value] ?? criteria.value
        case 'hasTextualDocuments':
        case 'hasLabResults':
        case 'hasHealthConstants':
        case 'hasDrugEvents':
          return criteria.value === '1' ? this.$t('automation.validationConditions.yes') : this.$t('automation.validationConditions.no')
        case 'patientAge':
          return this.$t('automation.validationConditions.patientAgeBetween', { min: criteria.value.split('_')[0], max: criteria.value.split('_')[1] })
        case 'stayDuration':
          return this.$t('automation.validationConditions.stayDurationBetween', { min: criteria.value.split('_')[0], max: criteria.value.split('_')[1] })
        case 'presentDocumentCategory':
        case 'absentDocumentCategory':
          return this.textualReportCategories[criteria.value] ?? criteria.value
        case 'fromStayDate':
        case 'fromLastSurgeryDate':
          return this.$t('automation.validationConditions.fromDate', { date: dayjs(criteria.value).format('DD/MM/YYYY') })
        case 'toStayDate':
        case 'toLastSurgeryDate':
          return this.$t('automation.validationConditions.toDate', { date: dayjs(criteria.value).format('DD/MM/YYYY') })
        default:
          return criteria.value
      }
    },
    getAutomationMissingCriteriaExplanation(criteriaGroupId, criteria) {
      const explanation = getAutomationExplanation(this.stay, this.automationGroup, this.automationDocumentCriteriaMatches)
      let response = 'x'
      if (!explanation.details[criteriaGroupId]) {
        return response
      }
      if (explanation.details[criteriaGroupId].match === true) {
        response = 'dash'
      }

      if (!explanation.details[criteriaGroupId].details[criteria.id]) {
        return response
      }

      if (explanation.details[criteriaGroupId].details[criteria.id].match === true) {
        response = 'check'
      }

      return response
    },
    isWaitingForDocumentCriteria(criteria) {
      return this.explainAutomationDocumentCriteriaRequest.fetching && _.includes(DOCUMENT_CRITERIA_TYPES, criteria.type)
    },
    displayableCriteriaList(criteriaList) {
      return  _.filter(criteriaList, (criteria) => (!criteria.type.startsWith('presentContent') && !criteria.type.startsWith('absentContent')))
    }
  }
}

</script>

<template>
  <div class="py-3">
    <div class="stay-list-criteria card">
      <div class="card-body">
        <div class="text-center">
          <h2>{{ $t('automation.validationConditions.title') }}</h2>
          <h3>{{ $t('automation.validationConditions.groupName') }} {{ automationGroup.name }}</h3>
          <h3>{{ $t('automation.validationConditions.stayId') }} {{ stay.rssId }}</h3>
        </div>
        <div
          v-for="(criteriaGroup, idx) in automationGroup.criteriaGroups"
          :key="criteriaGroup.id"
        >
          <div
            v-if="idx > 0 && displayableCriteriaList(criteriaGroup.criteriaList).length"
            class="m-2"
          >
            {{ automationGroup.searchOperators === 'andOr' ? 'ET' : 'OU' }}
          </div>
          <div
            v-if="displayableCriteriaList(criteriaGroup.criteriaList).length"
            class="stay-list-criteria card card-body"
          >
            <div
              v-for="(criteria, criteriaIdx) in displayableCriteriaList(criteriaGroup.criteriaList)"
              :key="criteria.id"
            >
              <div
                class="row align-items-start"
              >
                <div
                  class="col-2"
                >
                  <div
                    v-if="criteriaIdx > 0"
                  >
                    {{ automationGroup.searchOperators === 'andOr' ? 'OU' : 'ET' }}
                  </div>
                </div>
                <div
                  class="col"
                >
                  {{ getCriteriaType(criteria.type) }}
                </div>
                <div
                  class="col"
                >
                  {{ getCriteriaValue(criteria) }}
                </div>
                <div class="missing-criteria-check">
                  <div
                    v-if="isWaitingForDocumentCriteria(criteria)"
                    class="loader loader-xs"
                  />
                  <sancare-octicon
                    v-else
                    :name="getAutomationMissingCriteriaExplanation(criteriaGroup.id, criteria)"
                    :width="20"
                    :height="18"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="waitingForAnesthesia"
        >
          <hr>
          <span>
            {{ $t('automation.validationConditions.missingAnesthesia') }}
          </span>
        </div>
        <div
          v-if="waitingForAnapath"
        >
          <hr>
          <span>
            {{ $t('automation.validationConditions.missingBiopsy') }}
          </span>
        </div>
        <div
          v-if="stay.lastPredictionUpdate < stay.lastExternalPmsiUpdate"
        >
          <hr>
          <span
            class="m-2 p-2 badge-pill text-bg-secondary"
          >
            <sancare-octicon
              name="gear"
              :width="14"
              :height="14"
            />
            {{ $t('automation.validationConditions.predictionsUpdating') }}
          </span>
        </div>
        <div
          v-if="hasTextualCriteria"
        >
          <hr>
          <span
            class="m-2 p-2 badge-pill"
          >
            <sancare-octicon
              name="pencil"
              :width="14"
              :height="14"
            />
            {{ $t('automation.validationConditions.textualCriteriaWarning') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
h2 {
  font-size: 1.1rem;
}
</style>
